import { ReactNode, useEffect, useMemo, useState } from 'react';
import Big from 'big.js';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import { AgendaAppointment } from '../types/agenda.types';
import { PendingBillingOrder, PendingInvoice } from '../types/billing.types';
import { getPendingBillingOrders, getPendingInvoices } from '../services/AgendaService';
import { parseApiError } from '../../../services/apiError';
import { formatMoney } from '../../../utils/money';
import { EAppointmentStateValues, EAppointmentState } from '../../../enums/EAppointmentState';
import { formatTime } from '../utils/formatUtils';
import { getStatusInfo } from '../utils/statusUtils';
import './AppointmentDetailDrawer.scss';

const TOOLTIP_OPTIONS: TooltipOptions = { className: 'nv-tooltip', event: 'both', position: 'top' };

/** Dato de solo lectura: etiqueta arriba y valor como texto (vista de detalle del design system). */
function ReadOnlyField({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? 'nv-ro appt-drawer__wide' : 'nv-ro'}>
      <span className="nv-ro-label">{label}</span>
      <span className="nv-ro-value">{children}</span>
    </div>
  );
}

interface BillingAction {
  key: string;
  label: string;
  onClick: () => void;
}

interface AppointmentDetailDrawerProps {
  appointment: AgendaAppointment | null;
  visible: boolean;
  onHide: () => void;
  onCancelAppointment: (id: number, patientName: string, patientId?: number | null, professionalId?: number | null) => void;
  onRescheduleAppointment?: (id: number, patientName: string, patientId?: number | null, professionalId?: number | null) => void;
  onBilling?: (appointmentId: number) => void;
  /** Factura la orden del paciente que quedó pendiente de facturar. */
  onInvoicePendingOrder?: (orderId: number, patientId: number, patientName: string) => void;
  /** Cobra las facturas con saldo del paciente. */
  onCollect?: (patientId: number, patientName: string) => void;
  onEditPatient?: (appointmentId: number) => void;
  onViewClinicalHistory?: (patientId: number) => void;
  onViewLog?: (appointmentId: number, patientName: string) => void;
  onAssignPriority?: (appointmentId: number, patientName: string, currentPriorityId: number | null) => void;
  onRevertStatus?: (id: number) => void;
}

export default function AppointmentDetailDrawer({
  appointment,
  visible,
  onHide,
  onCancelAppointment,
  onRescheduleAppointment,
  onBilling,
  onInvoicePendingOrder,
  onCollect,
  onEditPatient,
  onViewClinicalHistory,
  onViewLog,
  onAssignPriority,
  onRevertStatus,
}: AppointmentDetailDrawerProps) {
  const appt = appointment;
  const [pendingOrders, setPendingOrders] = useState<PendingBillingOrder[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<PendingInvoice[]>([]);

  // Lo que falta facturar o cobrar del turno y del paciente
  useEffect(() => {
    setPendingOrders([]);
    setPendingInvoices([]);
    if (!visible || !appt?.patientcarequeue || !appt.medereentity) return undefined;
    let cancelled = false;
    getPendingBillingOrders(appt.patientcarequeue)
      .then((orders) => { if (!cancelled) setPendingOrders(orders); })
      .catch((error: unknown) => parseApiError(error));
    getPendingInvoices(appt.medereentity)
      .then((invoices) => { if (!cancelled) setPendingInvoices(invoices); })
      .catch((error: unknown) => parseApiError(error));
    return () => { cancelled = true; };
  }, [visible, appt?.patientcarequeue, appt?.medereentity]);

  const payableInvoices = pendingInvoices.filter((invoice) => invoice.payable);
  const pendingBalance = payableInvoices.reduce((sum, invoice) => sum.plus(invoice.pendingAmount), new Big(0));

  const statusInfo = useMemo(() => getStatusInfo(appt?.appointmentstateid ?? null), [appt?.appointmentstateid]);

  const duration = useMemo(() => {
    if (!appt?.appointmentfromtime || !appt?.appointmenttotime) return null;
    const [fh, fm] = appt.appointmentfromtime.split(':').map(Number);
    const [th, tm] = appt.appointmenttotime.split(':').map(Number);
    return (th * 60 + tm) - (fh * 60 + fm);
  }, [appt?.appointmentfromtime, appt?.appointmenttotime]);

  const isCancelable = useMemo(() => {
    const s = appt?.appointmentstateid;
    if (!s) return false;
    return (
      s === EAppointmentStateValues[EAppointmentState.SCHEDULLED] ||
      s === EAppointmentStateValues[EAppointmentState.CONFIRMED] ||
      s === EAppointmentStateValues[EAppointmentState.INQUEUE] ||
      s === EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION]
    );
  }, [appt?.appointmentstateid]);

  const isCompleted = appt?.appointmentstateid === EAppointmentStateValues[EAppointmentState.COMPLETED];

  /**
   * Como el "$" de la cola de Vaadin: la orden se registra cuando el paciente ya llegó. No va en un turno agendado,
   * confirmado, pendiente de pago o reprogramado, ni en uno ausente o cancelado.
   */
  const canRegisterOrder = useMemo(() => {
    const s = appt?.appointmentstateid;
    if (!s) return false;
    return ![
      EAppointmentState.SCHEDULLED, EAppointmentState.CONFIRMED, EAppointmentState.PENDING_PAYMENT,
      EAppointmentState.RESCHEDULLED, EAppointmentState.MISSED, EAppointmentState.CANCELLED_BY_PATIENT,
      EAppointmentState.CANCELLED_PENDING_CONTACT, EAppointmentState.CANCELLED_AND_NOTIFIED,
    ].some((state) => EAppointmentStateValues[state] === s);
  }, [appt?.appointmentstateid]);

  const isRevertable = useMemo(() => {
    const s = appt?.appointmentstateid;
    if (!s) return false;
    return (
      s === EAppointmentStateValues[EAppointmentState.INQUEUE] ||
      s === EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION] ||
      s === EAppointmentStateValues[EAppointmentState.INATTENTION] ||
      s === EAppointmentStateValues[EAppointmentState.IN_OBSERVATION]
    );
  }, [appt?.appointmentstateid]);

  if (!appt) return null;

  const fromTime = appt.appointmentfromtime ? appt.appointmentfromtime.substring(0, 5) : '';
  const toTime = appt.appointmenttotime ? appt.appointmenttotime.substring(0, 5) : '';
  const phone = appt.mobilephone || appt.phonenumber || '';
  const patientName = appt.patientname || '';
  const documentLabel = [appt.docshortname, appt.document].filter(Boolean).join(' ');
  const canEditPatient = Boolean(appt.medereentity && onEditPatient && appt.patientcarequeue && !isCompleted);

  // Cobro: una sola acción principal (la primera que corresponde en el circuito orden → factura → cobro);
  // las demás van como secundarias, a su izquierda.
  const billingActions: BillingAction[] = [];
  if (appt.patientcarequeue && appt.medereentity && onBilling && canRegisterOrder) {
    billingActions.push({ key: 'order', label: 'Registrar orden', onClick: () => onBilling(appt.patientcarequeue!) });
  }
  if (pendingOrders.length > 0 && appt.medereentity && onInvoicePendingOrder) {
    billingActions.push({
      key: 'invoice',
      label: 'Facturar',
      onClick: () => onInvoicePendingOrder(pendingOrders[0].orderId, appt.medereentity!, patientName),
    });
  }
  if (payableInvoices.length > 0 && appt.medereentity && onCollect) {
    billingActions.push({ key: 'collect', label: 'Cobrar', onClick: () => onCollect(appt.medereentity!, patientName) });
  }
  const [primaryAction, ...secondaryActions] = billingActions;

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      showCloseIcon={false}
      modal={false}
      dismissable
      className="nv-scope nv-drawer nv-drawer-s"
      aria-labelledby="appt-drawer-title"
    >
      <div className="nv-drawer-layout">
        <header className="nv-drawer-head">
          <div className="nv-stack-4">
            <h2 className="nv-modal-title" id="appt-drawer-title">{patientName || '—'}</h2>
            {(documentLabel || appt.clinichistoryid) && (
              <div className="nv-row-8 nv-muted">
                {documentLabel && <span>{documentLabel}</span>}
                {documentLabel && appt.clinichistoryid && <span aria-hidden>·</span>}
                {appt.clinichistoryid && (appt.medereentity && onViewClinicalHistory ? (
                  <Button
                    type="button"
                    link
                    label={`HC ${appt.clinichistoryid}`}
                    aria-label={`Ver historia clínica ${appt.clinichistoryid}`}
                    onClick={() => onViewClinicalHistory(appt.medereentity!)}
                  />
                ) : (
                  <span>HC {appt.clinichistoryid}</span>
                ))}
              </div>
            )}
          </div>
          <div className="appt-drawer__head-actions">
            {canEditPatient && (
              <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-pencil"
                aria-label="Editar datos del paciente"
                tooltip="Editar datos del paciente"
                tooltipOptions={TOOLTIP_OPTIONS}
                onClick={() => onEditPatient!(appt.patientcarequeue!)}
              />
            )}
            {onViewLog && appt.patientcarequeue && (
              <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-history"
                aria-label="Historial de cambios"
                tooltip="Historial de cambios"
                tooltipOptions={TOOLTIP_OPTIONS}
                onClick={() => onViewLog(appt.patientcarequeue!, patientName)}
              />
            )}
            <Button
              type="button"
              text
              className="nv-icon"
              icon="pi pi-times"
              aria-label="Cerrar"
              tooltip="Cerrar"
              tooltipOptions={TOOLTIP_OPTIONS}
              onClick={onHide}
            />
          </div>
        </header>

        <div className="nv-drawer-body">
          <section className="nv-section" aria-labelledby="appt-drawer-appointment">
            <div className="nv-section-head">
              <h3 className="nv-section-title" id="appt-drawer-appointment">Turno</h3>
              {isCancelable && (
                <div className="nv-row-8">
                  {onRescheduleAppointment && (
                    <Button
                      type="button"
                      link
                      label="Cambiar turno"
                      onClick={() => appt.patientcarequeue && onRescheduleAppointment(appt.patientcarequeue, patientName, appt.medereentity, appt.professionalmedereentity)}
                    />
                  )}
                  <Button
                    type="button"
                    link
                    label="Cancelar turno"
                    onClick={() => appt.patientcarequeue && onCancelAppointment(appt.patientcarequeue, patientName, appt.medereentity, appt.professionalmedereentity)}
                  />
                </div>
              )}
            </div>
            <div className="appt-drawer__data">
              <ReadOnlyField label="Estado">
                <span className="nv-row-8">
                  {statusInfo.label ? <Tag className={statusInfo.tagClass} value={statusInfo.label} /> : '—'}
                  {isRevertable && onRevertStatus && appt.patientcarequeue && (
                    // TODO(design-system): "Revertir estado" no está en la lista de íconos fijos por acción; se mantiene pi-undo.
                    <Button
                      type="button"
                      text
                      className="nv-icon"
                      icon="pi pi-undo"
                      aria-label="Revertir estado"
                      tooltip="Revertir estado"
                      tooltipOptions={TOOLTIP_OPTIONS}
                      onClick={() => onRevertStatus(appt.patientcarequeue!)}
                    />
                  )}
                </span>
              </ReadOnlyField>
              {fromTime && (
                <ReadOnlyField label="Horario">
                  {fromTime}{toTime && ` – ${toTime}`}{duration != null && duration > 0 && ` · ${duration} min`}
                </ReadOnlyField>
              )}
              {appt.reasonconsultation && (
                <ReadOnlyField label="Motivo" wide>{appt.reasonconsultation}</ReadOnlyField>
              )}
              <ReadOnlyField label="Prioridad">
                <span className="nv-row-8">
                  {appt.appointmentpriority ? <Tag className="nv-tag-prio" value={appt.appointmentpriority} /> : '—'}
                  {onAssignPriority && appt.patientcarequeue && !isCompleted && (
                    <Button
                      type="button"
                      text
                      className="nv-icon"
                      icon="pi pi-pencil"
                      aria-label="Cambiar prioridad"
                      tooltip="Cambiar prioridad"
                      tooltipOptions={TOOLTIP_OPTIONS}
                      onClick={() => onAssignPriority(appt.patientcarequeue!, patientName, appt.patientappointmentpriority)}
                    />
                  )}
                </span>
              </ReadOnlyField>
              <ReadOnlyField label="Profesional">{appt.professional || '—'}</ReadOnlyField>
              {appt.medicalspeciality && <ReadOnlyField label="Especialidad">{appt.medicalspeciality}</ReadOnlyField>}
              {appt.locationsname && <ReadOnlyField label="Lugar">{appt.locationsname}</ReadOnlyField>}
            </div>
          </section>

          {(phone || appt.email) && (
            <section className="nv-section" aria-labelledby="appt-drawer-contact">
              <h3 className="nv-section-title" id="appt-drawer-contact">Contacto</h3>
              <div className="appt-drawer__data">
                {phone && <ReadOnlyField label="Teléfono">{phone}</ReadOnlyField>}
                {appt.email && <ReadOnlyField label="Mail">{appt.email}</ReadOnlyField>}
              </div>
            </section>
          )}

          {appt.businessname && (
            <section className="nv-section" aria-labelledby="appt-drawer-coverage">
              <h3 className="nv-section-title" id="appt-drawer-coverage">Cobertura</h3>
              <div className="appt-drawer__data">
                <ReadOnlyField label="Obra social">{appt.businessname}</ReadOnlyField>
                {appt.planname && <ReadOnlyField label="Plan">{appt.planname}</ReadOnlyField>}
                {appt.benefitnumber && <ReadOnlyField label="N° de afiliado">{appt.benefitnumber}</ReadOnlyField>}
              </div>
            </section>
          )}

          {(appt.arrivaltime || appt.servedtime) && (
            <section className="nv-section" aria-labelledby="appt-drawer-times">
              <h3 className="nv-section-title" id="appt-drawer-times">Tiempos</h3>
              <div className="appt-drawer__data">
                {appt.arrivaltime && <ReadOnlyField label="Llegada">{formatTime(appt.arrivaltime)}</ReadOnlyField>}
                {appt.servedtime && <ReadOnlyField label="Inicio de atención">{formatTime(appt.servedtime)}</ReadOnlyField>}
              </div>
            </section>
          )}

          <section className="nv-section" aria-labelledby="appt-drawer-billing">
            <h3 className="nv-section-title" id="appt-drawer-billing">Facturación</h3>
            <div className="appt-drawer__billing">
              {!appt.hasbenefitorder && (
                <span className="nv-status-warn">
                  <i className="pi pi-exclamation-circle" aria-hidden />
                  Este turno todavía no tiene orden registrada.
                </span>
              )}
              {appt.hasbenefitorder && pendingOrders.length === 0 && payableInvoices.length === 0 && (
                <span className="nv-status-ok">
                  <i className="pi pi-check-circle" aria-hidden />
                  Turno con orden registrada
                </span>
              )}
              {pendingOrders.length > 0 && (
                <span className="nv-status-warn">
                  <i className="pi pi-exclamation-circle" aria-hidden />
                  La orden del paciente está pendiente de facturar ({formatMoney(pendingOrders[0].total)}).
                </span>
              )}
              {payableInvoices.length > 0 && (
                <span className="nv-status-warn">
                  <i className="pi pi-exclamation-circle" aria-hidden />
                  {payableInvoices.length === 1 ? 'El paciente tiene una factura con saldo' : `El paciente tiene ${payableInvoices.length} facturas con saldo`}
                  {' '}({formatMoney(pendingBalance)}).
                </span>
              )}
            </div>
          </section>
        </div>

        {primaryAction && (
          <footer className="nv-drawer-foot">
            {secondaryActions.map((action) => (
              <Button key={action.key} type="button" className="buttonMain2" outlined label={action.label} onClick={action.onClick} />
            ))}
            <Button type="button" className="buttonMain" label={primaryAction.label} onClick={primaryAction.onClick} />
          </footer>
        )}
      </div>
    </Sidebar>
  );
}
